import React, { useState } from 'react';
import './RockMarket.css';

export type RockType = 'rock' | 'stone' | 'gem' | 'meteorite' | 'asteroid';

export interface RockTypeConfig {
  name: string;
  emoji: string;
  basePrice: number;
  maxPrice: number;
  volatility: number;
}

export const ROCK_TYPES: Record<RockType, RockTypeConfig> = {
  rock: {
    name: 'Rock',
    emoji: '🪨',
    basePrice: 1,
    maxPrice: 50, // 50x multiplier
    volatility: 0.30, // Increased from 0.15
  },
  stone: {
    name: 'Stone',
    emoji: '🗿',
    basePrice: 10,
    maxPrice: 500, // 50x multiplier (increased from 20x)
    volatility: 0.35, // Increased from 0.18
  },
  gem: {
    name: 'Gem',
    emoji: '💎',
    basePrice: 50,
    maxPrice: 5000, // 100x multiplier (increased from 20x)
    volatility: 0.40, // Increased from 0.20
  },
  meteorite: {
    name: 'Meteorite',
    emoji: '☄️',
    basePrice: 500,
    maxPrice: 100000, // 200x multiplier (increased from 20x)
    volatility: 0.45, // Increased from 0.22
  },
  asteroid: {
    name: 'Asteroid',
    emoji: '🌌',
    basePrice: 50000,
    maxPrice: 25000000, // 500x multiplier (increased from 20x)
    volatility: 0.50, // Increased from 0.25
  },
};

export interface RockPrices {
  rock: number;
  stone: number;
  gem: number;
  meteorite: number;
  asteroid: number;
}

export interface RockHoldings {
  rock: number;
  stone: number;
  gem: number;
  meteorite: number;
  asteroid: number;
}

export interface RockPriceHistory {
  rock: number[];
  stone: number[];
  gem: number[];
  meteorite: number[];
  asteroid: number[];
}

interface RockMarketProps {
  holdings: RockHoldings;
  prices: RockPrices;
  priceHistory: RockPriceHistory;
  coins: number;
  onBuy: (type: RockType, amount: number) => void;
  onSell: (type: RockType, amount: number) => void;
}

const RockMarket: React.FC<RockMarketProps> = ({ holdings, prices, priceHistory, coins, onBuy, onSell }) => {
  const [selectedType, setSelectedType] = useState<RockType>('rock');
  const [buyAmount, setBuyAmount] = useState<string>('1');
  const [sellAmount, setSellAmount] = useState<string>('1');

  const currentConfig = ROCK_TYPES[selectedType];
  const currentPrice = prices[selectedType];
  const currentHoldings = holdings[selectedType];
  const currentHistory = priceHistory[selectedType] || [];

  const handleBuy = () => {
    const amount = parseInt(buyAmount, 10);
    if (amount > 0 && coins >= amount * currentPrice) {
      onBuy(selectedType, amount);
      setBuyAmount('1');
    }
  };

  const handleSell = () => {
    const amount = parseInt(sellAmount, 10);
    if (amount > 0 && currentHoldings >= amount) {
      onSell(selectedType, amount);
      setSellAmount('1');
    }
  };

  const maxBuyable = Math.floor(coins / currentPrice);
  const maxSellable = currentHoldings;

  const graphWidth = 200;
  const graphHeight = 80;
  const graphPadding = 10;
  const dataPoints = currentHistory.length > 0 ? [...currentHistory, currentPrice] : [currentPrice];
  const minPrice = dataPoints.length > 0 ? Math.min(...dataPoints) : currentPrice;
  const maxPrice = dataPoints.length > 0 ? Math.max(...dataPoints) : currentPrice;
  const priceRange = maxPrice - minPrice || 1;
  
  const getX = (index: number, total: number) => {
    if (total <= 1) return graphPadding;
    return graphPadding + (index / (total - 1)) * (graphWidth - graphPadding * 2);
  };
  
  const getY = (price: number) => {
    if (priceRange === 0) return graphHeight / 2;
    const normalized = (price - minPrice) / priceRange;
    return graphHeight - graphPadding - normalized * (graphHeight - graphPadding * 2);
  };
  
  const pathData = dataPoints.map((price, index) => {
    const x = getX(index, dataPoints.length);
    const y = getY(price);
    return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  const formatPrice = (price: number) => {
    if (price >= 1000000) {
      return `${(price / 1000000).toFixed(2)}M`;
    }
    if (price >= 1000) {
      return `${(price / 1000).toFixed(2)}K`;
    }
    return price.toFixed(0);
  };

  return (
    <div className="rock-market-container">
      <h3 className="rock-market-title">📈 Rock Market</h3>
      
      <div className="rock-type-tabs">
        {Object.entries(ROCK_TYPES).map(([type, config]) => (
          <button
            key={type}
            className={`rock-type-tab ${selectedType === type ? 'active' : ''}`}
            onClick={() => setSelectedType(type as RockType)}
          >
            {config.emoji} {config.name}
          </button>
        ))}
      </div>

      <div className="rock-market-info">
        <div className="rock-price-display">
          <span className="rock-price-label">Current {currentConfig.name} Price:</span>
          <span className="rock-price-value">{formatPrice(currentPrice)} 🪙</span>
        </div>
        <div className="rock-holdings">
          <span className="rock-holdings-label">Your {currentConfig.name}s:</span>
          <span className="rock-holdings-value">{currentHoldings} {currentConfig.emoji}</span>
        </div>
      </div>
      
      {dataPoints.length > 1 && (
        <div className="rock-price-graph">
          <h4 className="rock-graph-title">Price History (Last {Math.min(10, dataPoints.length)} ascensions)</h4>
          <svg width={graphWidth} height={graphHeight} className="rock-graph-svg">
            <defs>
              <linearGradient id="rockGraphGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#2ecc71', stopOpacity: 0.3 }} />
                <stop offset="100%" style={{ stopColor: '#2ecc71', stopOpacity: 0.05 }} />
              </linearGradient>
            </defs>
            <path
              d={`${pathData} L ${getX(dataPoints.length - 1, dataPoints.length)} ${graphHeight - graphPadding} L ${getX(0, dataPoints.length)} ${graphHeight - graphPadding} Z`}
              fill="url(#rockGraphGradient)"
            />
            <path
              d={pathData}
              fill="none"
              stroke="#2ecc71"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {dataPoints.map((price, index) => {
              const x = getX(index, dataPoints.length);
              const y = getY(price);
              return (
                <circle
                  key={index}
                  cx={x}
                  cy={y}
                  r="3"
                  fill="#2ecc71"
                  stroke="white"
                  strokeWidth="1.5"
                />
              );
            })}
          </svg>
          <div className="rock-graph-labels">
            <span className="rock-graph-min">{formatPrice(minPrice)} 🪙</span>
            <span className="rock-graph-max">{formatPrice(maxPrice)} 🪙</span>
          </div>
        </div>
      )}
      
      <div className="rock-market-actions">
        <div className="rock-action-section">
          <h4>Buy {currentConfig.name}s</h4>
          <div className="rock-input-group">
            <input
              type="number"
              min="1"
              max={maxBuyable}
              value={buyAmount}
              onChange={(e) => setBuyAmount(e.target.value)}
              className="rock-input"
            />
            <button
              className="rock-button rock-buy-button"
              onClick={handleBuy}
              disabled={maxBuyable === 0 || parseInt(buyAmount, 10) <= 0 || coins < parseInt(buyAmount, 10) * currentPrice}
            >
              Buy {buyAmount} {currentConfig.name}{parseInt(buyAmount, 10) !== 1 ? 's' : ''} ({formatPrice(parseInt(buyAmount, 10) * currentPrice)} 🪙)
            </button>
          </div>
          <p className="rock-hint">Max buyable: {maxBuyable} {currentConfig.name}s</p>
        </div>

        <div className="rock-action-section">
          <h4>Sell {currentConfig.name}s</h4>
          <div className="rock-input-group">
            <input
              type="number"
              min="1"
              max={maxSellable}
              value={sellAmount}
              onChange={(e) => setSellAmount(e.target.value)}
              className="rock-input"
            />
            <button
              className="rock-button rock-sell-button"
              onClick={handleSell}
              disabled={maxSellable === 0 || parseInt(sellAmount, 10) <= 0 || currentHoldings < parseInt(sellAmount, 10)}
            >
              Sell {sellAmount} {currentConfig.name}{parseInt(sellAmount, 10) !== 1 ? 's' : ''} (+{formatPrice(parseInt(sellAmount, 10) * currentPrice)} 🪙)
            </button>
          </div>
          <p className="rock-hint">Max sellable: {maxSellable} {currentConfig.name}s</p>
        </div>
      </div>
    </div>
  );
};

export default RockMarket;
